/*
 * installBaseFOTA.ts — Install base FOTA package on an IoT device
 *
 * Factory pattern: createInstallBaseFOTA(axiosPut, iotgroupurl) returns
 * an installBaseFOTA function scoped to the given API base URL and HTTP client.
 * This allows unit testing with mock dependencies and reuse from AngularJS
 * services without direct dependency on $http or $resource.
 */
import { IoT } from "common-library/types"

export const DEFAULT_FOTA_HOST = "iot-dev.retailanalysis.cloud"

/**
 * Check if a version string is >= the given major version.
 * @param version - Semver version string (e.g. "4.2.1")
 * @param minMajor - Minimum major version to compare against
 */
export function isVersionGte(version: string | undefined, minMajor: number): boolean {
  const match = (version || "").match(/^(\d+)/)
  if (!match) return false
  return parseInt(match[1], 10) >= minMajor
}

/**
 * Factory that creates an installBaseFOTA function with injected dependencies.
 * @param axiosPut - axios.put or equivalent (url, data, config) => Promise<{ data: unknown }>
 * @param iotgroupurl - Base URL for IoT group API (e.g. "/api/1.0/iot/ucountit")
 * @returns installBaseFOTA function
 */
export function createInstallBaseFOTA(
  axiosPut: (
    url: string,
    data: unknown,
    config: Record<string, unknown>
  ) => Promise<{ data: unknown }>,
  iotgroupurl: string
) {
  return function installBaseFOTA(
    device: IoT.Device.Data,
    fotahost?: string | null
  ): Promise<unknown> {
    if (!device?.uid) {
      return Promise.reject(new Error("Device uid is required"))
    }
    const platform = device.description?.properties?.device?.platform
    const fotaVersion = device.description?.fota?.version
    const host = fotahost ?? DEFAULT_FOTA_HOST
    let cmd: string
    if (isVersionGte(fotaVersion, 4)) {
      const appPath = device.description?.fota?.appPath
      if (!appPath) {
        return Promise.reject(
          new Error("Device fota appPath is required for FOTA version >= 4.0.0")
        )
      }
      cmd = `${appPath}/update_fota.sh &`
    } else if (platform === "ipn") {
      cmd = `curl -skL https://${host}/fota/public/start_fota.sh | bash - > /dev/null &`
    } else {
      return Promise.resolve()
    }
    const url = `${iotgroupurl}/${device.uid}/console`
    return axiosPut(url, {}, { params: { command: cmd, _: Date.now() } }).then((res) => res.data)
  }
}
